/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

/**
 * Determines whether an HTML element is visible to users and screen readers.
 * Checks various CSS properties and DOM states that affect visibility including
 * display, visibility, pointer events, dimensions, and accessibility attributes.
 *
 * @param element - The HTML element to check for visibility, or null if no element provided
 * @returns True if the element is visible and accessible, false otherwise
 */
export const isElementVisible = (element: HTMLElement | null): boolean => {
  if (!element) {
    return false;
  }

  const cs = getComputedStyle(element);

  if (cs.display === 'none') {
    return false;
  }

  if (cs.visibility === 'hidden') {
    return false;
  }

  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    return false;
  }

  if (element.closest('[aria-hidden="true"], [inert]')) {
    return false;
  }

  if (element.offsetParent === null && cs.position !== 'fixed') {
    return false;
  }

  return true;
};

/**
 * Determines whether an HTML element can receive keyboard focus.
 * Checks if the element exists, is not disabled, has a valid tabindex,
 * and meets tag-specific  requirements (e.g., anchor tags must have href).
 *
 * @param element - The HTML element to check, or null if no element provided
 * @returns True if the element can receive focus, false otherwise
 */
export const isElementFocusable = (element: HTMLElement | null) => {
  if (!element) {
    return false;
  }
  if (element.hasAttribute('disabled')) {
    return false;
  }
  const tabIndex = element.getAttribute('tabindex');
  if (tabIndex !== null && parseInt(tabIndex, 10) < 0) {
    return false;
  }
  if (element.tagName === 'A' && !element.hasAttribute('href')) {
    return false;
  }
  return true;
};

/**
 * Validates whether an HTML element should be focused based on accessibility and visibility requirements.
 * Checks if the element is both focusable (has appropriate tabindex, not disabled) and visible
 * (has dimensions, not hidden, and accessible to screen readers).
 *
 * @param element - The HTML element to validate for focus eligibility, or null if no element provided
 * @returns True if the element meets all requirements for focusing, false otherwise
 */
export const validateElementShouldBeFocused = (element: HTMLElement | null): boolean => {
  return !!element && isElementFocusable(element) && isElementVisible(element);
};

/**
 * Attempts to focus an element if it meets accessibility and visibility requirements.
 * Only focuses elements that are focusable (tabIndex > -1), visible (has offsetParent),
 * and not hidden from screen readers (no aria-hidden attribute).
 *
 * @param element - The HTML element to potentially focus, or null if no element provided
 */
export const focusOrIgnore = (element: HTMLElement | null) => {
  if (element && validateElementShouldBeFocused(element)) {
    element.focus();
  }
};

/**
 * Searches for the first interactive element within a given container element.
 * Looks for elements that can receive focus including buttons, links, form inputs,
 * and elements with explicit tabindex or contenteditable attributes.
 * Validates each candidate element to ensure it should be focused.
 *
 * @param element - The container element to search within for interactive elements
 * @param excludeSelector - Optional CSS selector to exclude elements from focus search.
 *                          Supports multiple selectors separated by commas (e.g., '[aria-haspopup="true"], .tooltip').
 *                          Only elements that directly match this selector will be skipped.
 * @returns The first valid interactive element found, or null if none exists or container is null
 */
export const searchForFirstInteractiveElement = (
  element: HTMLElement | null,
  excludeSelector?: string,
) => {
  if (element) {
    const candidates = element.querySelectorAll(
      'button:not([disabled]), a[href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), [contenteditable]:not([contenteditable="false"])',
    );

    for (const candidate of candidates) {
      const htmlElement = candidate as HTMLElement;
      if (excludeSelector && htmlElement.matches(excludeSelector)) {
        continue;
      }
      if (validateElementShouldBeFocused(htmlElement)) {
        return htmlElement;
      }
    }
  }
  return null;
};

/**
 * Searches for and focuses the first interactive element within a given container element.
 * Combines the functionality of finding the first focusable element and attempting to focus it
 * while respecting accessibility constraints.
 *
 * @param element - The container element to search within for interactive elements
 * @param excludeSelector - Optional CSS selector to exclude elements from focus search.
 *                          Supports multiple selectors separated by commas (e.g., '[aria-haspopup="true"], .tooltip').
 *                          Only elements that directly match this selector will be skipped.
 */
export const searchAndFocusFirstInteractiveElement = (
  element: HTMLElement | null,
  excludeSelector?: string,
) => {
  const firstInteractiveElement = searchForFirstInteractiveElement(element, excludeSelector);
  focusOrIgnore(firstInteractiveElement);
};

/**
 * Focuses an element and scrolls it into view with respect for user motion preferences.
 * Only scrolls if the element was successfully focused (passes accessibility and visibility checks).
 * Respects user's reduced motion preferences by using instant scrolling when preferred,
 * otherwise defaults to smooth scrolling behavior.
 *
 * @param element - The element to focus and scroll into view, or null to no-op
 * @param scrollOptions - Optional scroll behavior configuration for scrollIntoView.
 *                        The behavior property will be overridden to 'auto' if user prefers reduced motion.
 */
export const focusAndScrollIntoView = (
  element: HTMLElement | null,
  scrollOptions?: ScrollIntoViewOptions,
) => {
  if (!element || !validateElementShouldBeFocused(element)) {
    return;
  }

  element.focus();
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = prefersReducedMotion ? 'auto' : (scrollOptions?.behavior ?? 'smooth');

  element.scrollIntoView({
    block: 'center',
    ...scrollOptions,
    behavior,
  });
};

/**
 * Dictionary mapping semantic identifiers to their corresponding HTML element IDs.
 * Used for cross-referencing and maintaining consistent element identification across components.
 */
export const crossReferenceHtmlIdDictionary: Record<string, string> = {
  continueShoppingButtonId: 'continue-shopping-button',
  cartItemsContainerId: 'cart-items-container',
  cartItemIdPrefix: 'cart-item-id-',
  noItemsContainerId: 'no-items-container',
} as const;
